import { z } from "zod";
import type { ValidationT } from "./i18n";
import {
  DEFAULT_SETTLEMENT_CURRENCY,
  DEFAULT_SHARE_CURRENCY,
} from "./price-currency-defaults";

/**
 * Vendor ITEM-CREATE wizard schemas (factory form, R6 schema-factory pattern).
 *
 * Each factory takes a `validation`-namespace translator so error messages
 * resolve through next-intl. Module-scope schemas can't call useTranslations,
 * so the consuming component/page builds these via
 * useMemo(() => makeItemCreateSchema(t), [t]) with
 * const t = useTranslations("validation").
 */

// Step 1: basic info (with conditional service-location refine)
export const makeItemBasicInfoSchema = (t: ValidationT) =>
  z
    .object({
      isService: z.boolean().default(false),
      mainCategory: z
        .string({ message: t("mainCategoryRequired") })
        .min(1, t("mainCategoryRequired")),
      category_id: z
        .string({ message: t("subCategoryRequired") })
        .min(1, t("subCategoryRequired")),
      title: z
        .string({ message: t("titleRequired") })
        .min(1, t("titleRequired")),
      description: z
        .string({ message: t("descriptionRequired") })
        .min(1, t("descriptionRequired")),
      full_description: z
        .string({ message: t("fullDescriptionRequired") })
        .optional(),
      shippingCostType: z.enum(["free", "negotiable"]).default("free"),
      // Service-specific fields (optional)
      is_offline: z.boolean().optional(),
      is_online: z.boolean().optional(),
      service_time: z.string().optional(),
      service_location: z
        .array(
          z.object({
            name: z.string(),
            latitude: z.string(),
            longitude: z.string(),
          }),
        )
        .optional(),
    })
    .refine(
      (data) => {
        // If it's a service and offline is true, service_location is required
        if (data.isService && data.is_offline === true) {
          return (
            Array.isArray(data.service_location) &&
            data.service_location.length > 0
          );
        }
        return true;
      },
      {
        message: t("serviceLocationRequired"),
        path: ["service_location"],
      },
    );

// Step 2: images
export const makeItemImagesSchema = (t: ValidationT) =>
  z.object({
    productImages: z.array(z.any()).max(10, t("maxImages10")).optional(),
    productVideo: z.any().optional(),
  });

// Step 3: details (product-only attributes)
export const makeItemDetailsSchema = (_t: ValidationT) =>
  z.object({
    sku: z.string().optional().or(z.literal("")),
    brand_id: z.string().optional().or(z.literal("")),
    model_version: z.string().optional().or(z.literal("")),
    colors: z.array(z.string()).optional().default([]),
    sizes: z
      .array(
        z.object({
          value: z.string().optional().or(z.literal("")),
        }),
      )
      .optional()
      .default([]),
    specifications: z
      .array(
        z.object({
          name: z.string().optional().or(z.literal("")),
          value: z.string().optional().or(z.literal("")),
        }),
      )
      .optional()
      .default([{ name: "", value: "" }]),
  });

// Step 4: price
export const makeItemPriceSchema = (t: ValidationT) =>
  z.object({
    seller_share_unit: z.coerce
      .number({ message: t("sellerShareRequired") })
      .min(0, t("sellerShareNonNegative")),
    seller_share_currency: z
      .enum(["IRT", "UVM", "USDT"], {
        message: t("currencyTypeRequired"),
      })
      .default(DEFAULT_SHARE_CURRENCY),
    seller_settlement_currency: z
      .enum(["UVM", "USDT"], {
        message: t("settlementCurrencyTypeRequired"),
      })
      .default(DEFAULT_SETTLEMENT_CURRENCY),
    bv: z.coerce
      .number({ message: t("bvRequired") })
      .min(0, t("bvNonNegative")),
    quantity: z.coerce
      .number({ message: t("quantityRequired") })
      .int(t("quantityInteger"))
      .min(0, t("quantityNonNegative"))
      .optional(),
  });

// Combined schema for item creation form (all steps)
// Note: .refine() must be called AFTER .safeExtend() because .refine() transforms
// ZodObject into ZodEffects, and .safeExtend() only works with ZodObject
export const makeItemCreateSchema = (t: ValidationT) =>
  makeItemBasicInfoSchema(t)
    .safeExtend(makeItemDetailsSchema(t).shape)
    .safeExtend(makeItemImagesSchema(t).shape)
    .safeExtend(makeItemPriceSchema(t).shape)
    .refine(
      (data) => {
        // productImages is required only when isService is false (i.e., it's a product)
        if (!data.isService) {
          return (
            data.productImages !== undefined &&
            Array.isArray(data.productImages) &&
            data.productImages.length > 0
          );
        }
        return true;
      },
      {
        message: t("atLeastOneImageRequired"),
        path: ["productImages"],
      },
    )
    .refine(
      (data) => {
        // quantity is required only when isService is false (i.e., it's a product)
        if (!data.isService) {
          return (
            data.quantity !== undefined &&
            data.quantity !== null &&
            typeof data.quantity === "number"
          );
        }
        return true;
      },
      {
        message: t("quantityRequired"),
        path: ["quantity"],
      },
    );

// Type inference for each step
export type ItemBasicInfoFormData = z.infer<
  ReturnType<typeof makeItemBasicInfoSchema>
>;
export type ItemImagesFormData = z.infer<
  ReturnType<typeof makeItemImagesSchema>
>;
export type ItemDetailsFormData = z.infer<
  ReturnType<typeof makeItemDetailsSchema>
>;
export type ItemPriceFormData = z.infer<ReturnType<typeof makeItemPriceSchema>>;

// Type inference for item creation form (combined)
export type ItemCreateFormData = z.infer<
  ReturnType<typeof makeItemCreateSchema>
>;

// Specification item type
export type SpecificationItem = {
  name: string;
  value: string;
};

// Size item type
export type SizeItem = {
  value: string;
};

// Variable attribute item type
export type VariableAttributeItem = {
  name: string;
  value: string;
};
